import { useState, useEffect } from 'react'
import { Card, CardBody, CardHeader, Spinner, Button as HeroButton } from '@heroui/react'
import { Badge } from '@trafiflow/shared-ui'
import { useNavigate } from 'react-router-dom'
import { alertsApi, type AlertEvent } from '../../lib/funnel-api'
import { formatRelativeTime } from '../../lib/format-changes'

function getSeverityVariant(severity: string) {
  switch (severity) {
    case 'critical': return 'critical' as const
    case 'warning': return 'warning' as const
    default: return 'default' as const
  }
}

export function RecentAlerts() {
  const [alerts, setAlerts] = useState<AlertEvent[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    async function load() {
      try {
        const response = await alertsApi.listEvents()
        const sorted = (response.data ?? [])
          .slice()
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 5)
        setAlerts(sorted)
      } catch {
        // Silently fail — dashboard section is non-critical
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) {
    return (
      <Card>
        <CardBody className="flex items-center justify-center py-8">
          <Spinner size="sm" color="primary" />
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex items-center justify-between px-6 pt-5 pb-0">
        <h2 className="text-lg font-semibold text-foreground">Recent Alerts</h2>
        <HeroButton
          variant="light"
          color="primary"
          size="sm"
          onPress={() => navigate('/funnels/alerts')}
        >
          View All
        </HeroButton>
      </CardHeader>
      <CardBody className="px-6 pb-5">
        {alerts.length === 0 ? (
          <p className="text-sm text-default-500 text-center py-4">No recent alerts</p>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className="flex items-start gap-3 p-3 rounded-lg border border-default-200 hover:bg-content2 transition-colors"
              >
                <Badge variant={getSeverityVariant(alert.severity)}>
                  {alert.severity}
                </Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{alert.message}</p>
                  <p className="text-xs text-default-400 mt-0.5">
                    {formatRelativeTime(alert.createdAt)}
                  </p>
                </div>
                {!alert.isRead && (
                  <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                )}
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}
